import React from "react";
import styled from "styled-components";
import { FiSearch } from "react-icons/fi";
import { colors } from "./colors";
import { MEASURES, exportDropsCsv, measureOf } from "./drops";
import Segmented from "./Segmented";
import Card, { Title, Subtitle } from "./Card";
import Filters, { OutlineButton } from "./Filters";
import Row, { hiddenBelow } from "./Row";
import SortableHeader from "./SortableHeader";
import ChangeWindowBar from "./ChangeWindowBar";

export default function Table({ drops, measure, onMeasureChange, causeOptions, site, changeEntries, total, filters, severityCounts, hasActiveFilters, sort, selectedId,
                                  onFilterChange, onResetFilters, onSort, onSelect }) {
    return (
        <Card as="div">
            <Head>
                <div>
                    <Title id="drops-heading">
                        Detected drops
                        <Count>{total}</Count>
                    </Title>
                    <Subtitle>The Issues page’s list · {measureOf(measure).unit} a week, the 30 days before the issue started vs. the 30 days from it</Subtitle>
                </div>
                <HeadSide>
                    <Segmented ariaLabel="Measure" options={MEASURES} value={measure} onChange={onMeasureChange} />
                    <Hint>Select a row to see the diagnosis</Hint>
                </HeadSide>
            </Head>

            {filters.changeWindow && (
                <ChangeWindowBar window={filters.changeWindow} entries={changeEntries} matchCount={drops.length} onClear={() => onFilterChange("changeWindow", null)} />
            )}

            <Filters filters={filters} causeOptions={causeOptions} severityCounts={severityCounts} hasActiveFilters={hasActiveFilters} onChange={onFilterChange} onReset={onResetFilters}
                     onExport={() => exportDropsCsv(drops, site.domain, measure)} exportDisabled={drops.length === 0} />

            <Scroll>
                <Grid aria-labelledby="drops-heading">
                    <Thead>
                        <tr>
                            <SortableHeader label="Severity" sortKey="severity" sort={sort} onSort={onSort} style={{ width: 116, paddingLeft: 20 }} />
                            <Th>Affected</Th>
                            <Th $from={1100} style={{ width: 160 }}>Likely cause</Th>
                            <Th $from={1100} $right style={{ width: 132 }}>{measureOf(measure).label} / wk</Th>
                            <SortableHeader label="Change" sortKey="change" sort={sort} onSort={onSort} align="right" style={{ width: 108, textAlign: "right" }} />
                            <Th $from={900} style={{ width: 132 }}>6 months around the start</Th>
                            <SortableHeader label="Started" sortKey="detected" sort={sort} onSort={onSort} className="detected" style={{ width: 100 }} />
                            <Th style={{ width: 40 }}><Hidden>Open</Hidden></Th>
                        </tr>
                    </Thead>
                    <tbody>
                        {drops.map((drop) => <Row key={drop.id} drop={drop} measure={measure} today={site.today} selected={drop.id === selectedId} onSelect={onSelect} />)}
                    </tbody>
                </Grid>
                {drops.length === 0 && (
                    <Empty>
                        <EmptyIcon aria-hidden><FiSearch /></EmptyIcon>
                        <EmptyTitle>{filters.changeWindow ? "No drops on the URLs changed in this window" : "No drops match these filters"}</EmptyTitle>
                        <EmptyText>{filters.changeWindow ? "These page changes don’t appear to have cost traffic." : "Try a different severity or clear your search."}</EmptyText>
                        <OutlineButton type="button" onClick={onResetFilters} style={{ marginTop: 16 }}>Reset filters</OutlineButton>
                    </Empty>
                )}
            </Scroll>
        </Card>
    );
}

const Head = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px;
    padding: 20px 20px 12px;
`;

const Count = styled.span`
    margin-left: 8px;
    border-radius: 6px;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    background: ${colors.canvas};
    color: ${colors.muted};
`;

const HeadSide = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
`;

const Hint = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${colors.subtle};
`;

const Scroll = styled.div`
    overflow-x: auto;
`;

const Grid = styled.table`
    width: 100%;
    min-width: 640px;
    table-layout: fixed;
    border-collapse: collapse;
    text-align: left;
    @media (max-width: 1279px) { th.detected { display: none; } }
`;

const Thead = styled.thead`
    font-size: 12px;
    color: ${colors.muted};
    background: ${colors.canvas}99;
    th { padding: 10px 12px 10px 0; font-weight: 500; }
`;

const Th = styled.th`
    text-align: ${({ $right }) => ($right ? "right" : "left")};
    ${({ $from }) => hiddenBelow($from)}
`;

const Hidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
`;

const Empty = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 56px 24px;
    text-align: center;
`;

const EmptyIcon = styled.span`
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    font-size: 20px;
    background: ${colors.canvas};
    color: ${colors.subtle};
`;

const EmptyTitle = styled.p`
    margin: 12px 0 0;
    font-size: 14px;
    font-weight: 500;
    color: ${colors.ink};
`;

const EmptyText = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    color: ${colors.muted};
`;
